import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Spinner, cx } from "@/components/ui";

export interface BannerTile {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}

/**
 * Compact, horizontally-scrollable summary band used as a glanceable shortcut into
 * the full Stats tab. Deliberately slim (vs. the larger vertical `StatCard` on the
 * Stats page). Callers supply already-formatted tiles + a deep-link CTA; the values
 * should come from the SAME endpoints/query-keys the Stats tab uses so the figures
 * stay identical.
 */
export function StatsBanner({
  title,
  tiles,
  to,
  cta,
  loading = false,
  className,
}: {
  title?: string;
  tiles: BannerTile[];
  to: string;
  cta: string;
  loading?: boolean;
  className?: string;
}) {
  return (
    <div className={cx("card p-4", className)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        {title ? (
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
        ) : (
          <span />
        )}
        <Link to={to} className="shrink-0 text-sm font-medium text-brand-600 hover:underline">
          {cta} →
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-4 text-brand-600">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="flex gap-6 overflow-x-auto pb-1">
          {tiles.map((t, i) => (
            <div key={i} className="shrink-0">
              <div className="text-xl font-bold tracking-tight text-ink">{t.value}</div>
              <div className="mt-0.5 text-xs text-ink-faint">{t.label}</div>
              {t.sub != null && <div className="text-[11px] text-ink-faint/80">{t.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
